export interface URLState {
  page?: number;
  search?: string;
  filters?: number[];
  [key: string]: any;
}

export interface URLUpdateOptions {
  replace?: boolean; // Use replaceState instead of pushState
  resetPage?: boolean; // Reset page to 1 when other params change
}

export class URLStateManager {
  private readonly DEFAULT_PAGE = 1;

  /**
   * Get current URL parameters as an object
   */
  getCurrentState(): URLState {
    const params = new URLSearchParams(window.location.search);
    const state: URLState = {};

    // Get page
    const page = params.get('page');
    if (page) {
      const pageNum = parseInt(page);
      if (!isNaN(pageNum) && pageNum > 0) {
        state.page = pageNum;
      }
    }

    // Get search query
    const search = params.get('search');
    if (search) {
      state.search = search;
    }

    // Get filters
    const filters = params.get('filter');
    if (filters) {
      state.filters = filters
        .split(',')
        .map((f) => parseInt(f))
        .filter((f) => !isNaN(f));
    }

    return state;
  }

  /**
   * Update URL state
   */
  updateState(newState: URLState, options: URLUpdateOptions = {}): void {
    const currentParams = new URLSearchParams(window.location.search);
    const { replace = false, resetPage = false } = options;

    this.updatePageParam(currentParams, newState, resetPage);
    this.updateSearchParam(currentParams, newState);
    this.updateFiltersParam(currentParams, newState);
    this.updateCustomParams(currentParams, newState);
    this.updateBrowserHistory(currentParams, replace);
  }

  private updatePageParam(params: URLSearchParams, newState: URLState, resetPage: boolean): void {
    if (newState.page !== undefined) {
      if (newState.page === this.DEFAULT_PAGE) {
        params.delete('page');
      } else {
        params.set('page', newState.page.toString());
      }
    } else if (resetPage) {
      params.delete('page');
    }
  }

  private updateSearchParam(params: URLSearchParams, newState: URLState): void {
    if (newState.search !== undefined) {
      if (newState.search === '' || newState.search === null) {
        params.delete('search');
      } else {
        params.set('search', newState.search);
      }
    }
  }

  private updateFiltersParam(params: URLSearchParams, newState: URLState): void {
    if (newState.filters !== undefined) {
      if (newState.filters.length === 0) {
        params.delete('filter');
      } else {
        params.set('filter', newState.filters.join(','));
      }
    }
  }

  private updateCustomParams(params: URLSearchParams, newState: URLState): void {
    Object.keys(newState).forEach((key) => {
      if (!['page', 'search', 'filters'].includes(key)) {
        const value = newState[key];
        if (value === undefined || value === null || value === '') {
          params.delete(key);
        } else {
          params.set(key, value.toString());
        }
      }
    });
  }

  private updateBrowserHistory(params: URLSearchParams, replace: boolean): void {
    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    if (replace) {
      window.history.replaceState({ path: newUrl }, '', newUrl);
    } else {
      window.history.pushState({ path: newUrl }, '', newUrl);
    }

    // Dispatch a custom event to trigger state sync since pushState doesn't trigger popstate
    window.dispatchEvent(new CustomEvent('urlstatechange', { detail: this.getCurrentState() }));
  }

  /**
   * Get current page number
   */
  getCurrentPage(): number {
    const state = this.getCurrentState();
    return state.page ?? this.DEFAULT_PAGE;
  }

  /**
   * Set page number
   */
  setPage(page: number, options: URLUpdateOptions = {}): void {
    this.updateState({ page }, options);
  }

  /**
   * Get current search query
   */
  getSearchQuery(): string {
    const state = this.getCurrentState();
    return state.search ?? '';
  }

  /**
   * Set search query
   */
  setSearchQuery(query: string, options: URLUpdateOptions = { resetPage: true }): void {
    this.updateState({ search: query }, options);
  }

  /**
   * Get current active filters
   */
  getActiveFilters(): number[] {
    const state = this.getCurrentState();
    return state.filters || [];
  }

  /**
   * Set active filters
   */
  setActiveFilters(filters: number[], options: URLUpdateOptions = { resetPage: true }): void {
    this.updateState({ filters }, options);
  }

  /**
   * Add a filter
   */
  addFilter(filterId: number, options: URLUpdateOptions = { resetPage: true }): void {
    const currentFilters = this.getActiveFilters();
    if (!currentFilters.includes(filterId)) {
      this.setActiveFilters([...currentFilters, filterId], options);
    }
  }

  /**
   * Remove a filter
   */
  removeFilter(filterId: number, options: URLUpdateOptions = { resetPage: true }): void {
    const currentFilters = this.getActiveFilters();
    const newFilters = currentFilters.filter((f) => f !== filterId);
    this.setActiveFilters(newFilters, options);
  }

  /**
   * Toggle a filter
   */
  toggleFilter(filterId: number, options: URLUpdateOptions = { resetPage: true }): void {
    const currentFilters = this.getActiveFilters();
    if (currentFilters.includes(filterId)) {
      this.removeFilter(filterId, options);
    } else {
      this.addFilter(filterId, options);
    }
  }

  /**
   * Clear all filters
   */
  clearFilters(options: URLUpdateOptions = { resetPage: true }): void {
    this.setActiveFilters([], options);
  }

  /**
   * Clear search query
   */
  clearSearch(options: URLUpdateOptions = { resetPage: true }): void {
    this.setSearchQuery('', options);
  }

  /**
   * Reset all URL state to defaults
   */
  reset(): void {
    this.updateState(
      {
        page: this.DEFAULT_PAGE,
        search: '',
        filters: [],
      },
      { replace: true }
    );
  }

  /**
   * Get a specific URL parameter
   */
  getParameter(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  }

  /**
   * Set a specific URL parameter
   */
  setParameter(key: string, value: string, options: URLUpdateOptions = {}): void {
    this.updateState({ [key]: value }, options);
  }

  /**
   * Remove a specific URL parameter
   */
  removeParameter(key: string, options: URLUpdateOptions = {}): void {
    this.updateState({ [key]: undefined }, options);
  }

  /**
   * Check if a specific filter is active
   */
  isFilterActive(filterId: number): boolean {
    return this.getActiveFilters().includes(filterId);
  }

  /**
   * Get URL state for API requests
   */
  getAPIParams(): {
    page: number;
    search?: string;
    tags?: string;
  } {
    const state = this.getCurrentState();
    const params: any = {
      page: state.page ?? this.DEFAULT_PAGE,
    };

    if (state.search) {
      params.search = state.search;
    }

    if (state.filters && state.filters.length > 0) {
      params.tags = state.filters.join(',');
    }

    return params;
  }

  /**
   * Build URL string with given state
   */
  buildURL(state: URLState, baseUrl?: string): string {
    const params = new URLSearchParams();

    if (state.page && state.page !== this.DEFAULT_PAGE) {
      params.set('page', state.page.toString());
    }

    if (state.search) {
      params.set('search', state.search);
    }

    if (state.filters && state.filters.length > 0) {
      params.set('filter', state.filters.join(','));
    }

    // Add any custom parameters
    Object.keys(state).forEach((key) => {
      if (!['page', 'search', 'filters'].includes(key) && state[key] !== undefined) {
        params.set(key, state[key].toString());
      }
    });

    const base = baseUrl ?? window.location.pathname;
    const queryString = params.toString();

    return queryString ? `${base}?${queryString}` : base;
  }

  /**
   * Listen for browser back/forward navigation and programmatic URL changes
   */
  onStateChange(callback: (state: URLState) => void): () => void {
    const handlePopState = () => {
      callback(this.getCurrentState());
    };

    const handleURLStateChange = (event: CustomEvent) => {
      callback(event.detail);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('urlstatechange', handleURLStateChange as EventListener);

    // Return cleanup function
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('urlstatechange', handleURLStateChange as EventListener);
    };
  }

  /**
   * Scroll to top of page or specific element
   */
  scrollToTop(elementId?: string, behavior: ScrollBehavior = 'smooth'): void {
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior, block: 'start' });
      }
    } else {
      window.scrollTo({ top: 0, behavior });
    }
  }
}
